import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import { pdf } from '@react-pdf/renderer';

// Register fonts
Font.register({
  family: 'Open Sans',
  fonts: [
    {
      src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf',
      fontWeight: 'normal',
    },
    {
      src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-600.ttf',
      fontWeight: 'semibold',
    },
    {
      src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-700.ttf',
      fontWeight: 'bold',
    },
    {
      src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-800.ttf',
      fontWeight: 'extrabold',
    }
  ],
});

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 40,
    backgroundColor: '#FFFFFF',
    fontFamily: 'Open Sans',
  },
  sectionTitle: {
    backgroundColor: '#20419b',
    color: '#fff',
    fontSize: 12,
    paddingLeft: 2,
    marginBottom: 10,
    marginTop: 15,
    textTransform: 'uppercase',
    fontFamily: 'Open Sans',
    fontWeight: 'bold',
  },
  fieldRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 4,
    breakInside: 'avoid',
  },
  subheaderRow: {
    width: '100%',
    marginBottom: 8,
    marginTop: 8,
  },
  // Base field style
  field: {
    fontSize: 8,
    padding: 4,
  },
  // Specific field span styles
  fieldSpan24: {
    flex: '1 1 100%',
  },
  fieldSpan12: {
    flex: '1 1 50%',
  },
  fieldSpan8: {
    flex: '1 1 33.33%',
  },
  fieldSpan6: {
    flex: '1 1 25%',
  },
  fieldLabel: {
    fontFamily: 'Open Sans',
    fontWeight: 'bold',
    fontSize: 8,
  },
  title: {
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 20,
    color: '#284095',
    fontFamily: 'Open Sans',
    fontWeight: 'extrabold',
  },
  section: {
    marginBottom: 15,
    breakInside: 'avoid-page',
  },
  disclaimer: {
    fontSize: 8,
    marginTop: 20,
    fontFamily: 'Open Sans',
  },
  subheader: {
    fontSize: 10,
    fontFamily: 'Open Sans',
    fontWeight: 'bold',
    color: '#000',
    paddingVertical: 4,
    width: '100%',
    marginTop: 10
  },
  fieldContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginVertical: 2,
    breakInside: 'avoid',
  },
  fieldValueContainer: {
    flex: 1,
    position: 'relative',
    marginLeft: 4,
  },
  fieldValue: {
    fontSize: 8,
    paddingBottom: 2,
    fontFamily: 'Open Sans',
  },
  fieldLine: {
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    borderBottomStyle: 'solid',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  checkboxGroup: {
    flexDirection: 'column',
    gap: 4,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  checkbox: {
    width: 10,
    height: 10,
    border: '1px solid black',
    marginRight: 4,
    position: 'relative',
  },
  checkboxChecked: {
    backgroundColor: '#000',
    width: 6,
    height: 6,
    position: 'absolute',
    top: 1,
    left: 1,
  },
  checkboxLabel: {
    fontSize: 8,
    fontFamily: 'Open Sans',
  },
  textArea: {
    border: '1px solid black',
    padding: 4,
    minHeight: 60,
    marginTop: 4,
  },
  signatureSection: {
    marginTop: 20,
  },
  signatureText: {
    fontSize: 8,
    marginBottom: 10,
    fontFamily: 'Open Sans',
  },
  signatureLine: {
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    borderBottomStyle: 'solid',
    marginTop: 20,
  },
  typographyContainer: {
    marginVertical: 8,
  },
  typographyTitle: {
    fontSize: 10,
    fontFamily: 'Open Sans',
    fontWeight: 'bold',
    marginBottom: 4,
  },
  typographyContent: {
    fontSize: 8,
    fontFamily: 'Open Sans',
    lineHeight: 1.4,
  },
  radioGroup: {
    flexDirection: 'row',
    gap: 12,
  },
  radioRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  radio: {
    width: 10,
    height: 10,
    borderRadius: 5,
    border: '1px solid black',
    marginRight: 4,
    position: 'relative',
  },
  radioChecked: {
    backgroundColor: '#000',
    width: 4,
    height: 4,
    borderRadius: 2,
    position: 'absolute',
    top: 2,
    left: 2,
  },
  radioLabel: {
    fontSize: 8,
    fontFamily: 'Open Sans',
  },
  signatureField: {
    marginTop: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    borderBottomStyle: 'solid',
    paddingBottom: 2,
  },
});

export const RequestPDF = ({ formData, title }) => {
  // Helper function to get field style based on span
  const getFieldStyle = (span) => {
    const baseStyle = styles.field;
    switch (span) {
      case 24:
        return { ...baseStyle, ...styles.fieldSpan24 };
      case 12:
        return { ...baseStyle, ...styles.fieldSpan12 };
      case 8:
        return { ...baseStyle, ...styles.fieldSpan8 };
      case 6:
        return { ...baseStyle, ...styles.fieldSpan6 };
      default:
        return { ...baseStyle, ...styles.fieldSpan12 };
    }
  };

  // Update the field rendering to handle different field types
  const renderField = (field) => {
    switch (field.type) {
      case 'checkbox-group':
        return (
          <View style={styles.checkboxGroup}>
            {field.options.map((option, idx) => (
              <View key={idx} style={styles.checkboxRow}>
                <View style={styles.checkbox}>
                  {option.checked && <View style={styles.checkboxChecked} />}
                </View>
                <Text style={styles.checkboxLabel}>{option.label}</Text>
                {option.hasInput && (
                  <View style={styles.fieldValueContainer}>
                    <Text style={styles.fieldValue}>{field.otherValue || ''}</Text>
                    <View style={styles.fieldLine} />
                  </View>
                )}
              </View>
            ))}
          </View>
        );
      case 'textarea':
        return <View style={styles.textArea}>
          <Text style={styles.fieldValue}>{field.value || ''}</Text>
        </View>;
      case 'radio-group':
        return (
          <View style={styles.radioGroup}>
            {field.options.map((option, idx) => (
              <View key={idx} style={styles.radioRow}>
                <View style={styles.radio}>
                  {option.checked && <View style={styles.radioChecked} />}
                </View>
                <Text style={styles.radioLabel}>{option.label}</Text>
              </View>
            ))}
          </View>
        );
      default:
        return (
          <>
            <Text style={styles.fieldValue}>{field.value || ' '}</Text>
            <View style={styles.fieldLine} />
          </>
        );
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View wrap={false}>
          <Text style={styles.title}>{title}</Text>
          
          {formData.slice(0, 4).map((section, sectionIndex) => (
            <View key={sectionIndex} style={styles.section} wrap>
              <Text style={styles.sectionTitle}>{section.title}</Text>
              <View style={styles.fieldRow} wrap>
                {section.fields.map((field, fieldIndex) => (
                  field.isSubheader ? (
                    <Text key={fieldIndex} style={styles.subheader}>
                      {field.label}
                    </Text>
                  ) : field.type === 'typography' ? (
                    <View key={fieldIndex} style={getFieldStyle(field.span)}>
                      <View style={styles.typographyContainer}>
                        <Text style={styles.typographyTitle}>{field.title}</Text>
                        <Text style={styles.typographyContent}>{field.content}</Text>
                      </View>
                    </View>
                  ) : (
                    <View key={fieldIndex} style={getFieldStyle(field.span)} wrap>
                      <View style={styles.fieldContainer}>
                        <Text style={styles.fieldLabel}>{field.label}: </Text>
                        <View style={styles.fieldValueContainer}>
                          {renderField(field)}
                        </View>
                      </View>
                    </View>
                  )
                ))}
              </View>
            </View>
          ))}
        </View>
      </Page>

      <Page size="A4" style={styles.page}>
        <View>
         {formData.slice(4).map((section, sectionIndex) => (
            <View key={sectionIndex} style={styles.section} wrap>
              <Text style={styles.sectionTitle}>{section.title}</Text>
              <View style={styles.fieldRow} wrap>
                {section.fields.map((field, fieldIndex) => (
                  field.isSubheader ? (
                    <Text key={fieldIndex} style={styles.subheader}>
                      {field.label}
                    </Text>
                  ) : field.type === 'typography' ? (
                    <View key={fieldIndex} style={getFieldStyle(field.span)}>
                      <View style={styles.typographyContainer}>
                        <Text style={styles.typographyTitle}>{field.title}</Text>
                        <Text style={styles.typographyContent}>{field.content}</Text>
                      </View>
                    </View>
                  ) : (
                    <View key={fieldIndex} style={getFieldStyle(field.span)} wrap>
                      <View style={styles.fieldContainer}>
                        <Text style={styles.fieldLabel}>{field.label}: </Text>
                        <View style={styles.fieldValueContainer}>
                          {renderField(field)}
                        </View>
                      </View>
                    </View>
                  )
                ))}
              </View>
            </View>
          ))}

          <Text style={styles.disclaimer}>
            Disclaimer: This resource provides information from a complex and evolving medical coding system. The treating physician is solely responsible for diagnosis coding and determination of the appropriate ICD-10-CM codes that describe the patient’s condition and are supported by the medical record. All codes listed in this guide are for informational purposes and are not an exhaustive list. The CPT, HCPCS, and ICD-10-CM codes provided are based on AMA or CMS guidelines. The billing party is solely responsible for coding of services (eg, CPT coding). Because government and other third-party payer coding requirements change periodically, please verify current coding requirements directly with the payer being billed.
          </Text>
          <Text style={styles.disclaimer}>
          Confidentiality Notice: This facsimile transmission may contain confidential or legally privileged information that is intended only for the individual or entity named on this transmission sheet. If you are not the intended recipient, you are notified that any disclosure, copying, distribution, or reliance upon the contents of this facsimile is strictly prohibited. If you have received this facsimile in error, please notify us immediately by telephone so that we can arrange for the return of the transmitted materials to us at no cost to you.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

// Add this export to generate PDF blob
export const generatePDF = async (formData, title) => {
  const doc = <RequestPDF formData={formData} title={title} />;
  const blob = await pdf(doc).toBlob();
  return blob;
};

export default RequestPDF;